<template>
  <v-card
    class="checklist-editor"
  >
    <v-banner
      icon="mdi-format-list-checks"
      single-line
    >
      <div>
        <h3>
          <span
            v-if="hasItems"
          >
            <v-text-field
              v-model="editedChecklist.title"
              dense
              :disabled="!editable"
              hide-details
              outlined
            />
          </span>
          <slot name="title">
            Checklist
          </slot>
        </h3>

        <slot name="subtitle" />
      </div>
      <template #actions>
        <template v-if="editable">
          <v-tooltip
            v-if="!disableCustomList"
            bottom
          >
            <template #activator="{on, attrs}">
              <v-btn
                icon
                outlined
                v-bind="attrs"
                v-on="on"
                @click="addItem"
              >
                <v-icon>mdi-playlist-plus</v-icon>
              </v-btn>
            </template>
            <span>Add Item</span>
          </v-tooltip>

          <v-menu
            v-if="hasItems && !disableCustomList"
            bottom
            left
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list
              dense
            >
              <v-list-item
                v-if="!isNewChecklist"
                @click="reloadChecklist(checklist)"
              >
                <v-list-item-title>
                  <v-icon
                    left
                  >
                    mdi-restore
                  </v-icon>
                  Reset
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                @click="deleteChecklist"
              >
                <v-list-item-title>
                  <v-icon
                    left
                  >
                    mdi-delete
                  </v-icon>
                  Delete
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <v-tooltip
          v-if="!hasItems"
          bottom
        >
          <template #activator="{on, attrs}">
            <v-btn
              icon
              outlined
              v-bind="attrs"
              v-on="on"
              @click="openImportChecklistDialog"
            >
              <v-icon>mdi-playlist-star</v-icon>
            </v-btn>
          </template>
          <span>Add Checklist from Library</span>
        </v-tooltip>
      </template>
    </v-banner>

    <v-list
      class="py-0"
      color="transparent"
      dense
    >
      <v-list-item
        v-if="!hasItems"
      >
        <v-list-item-content>
          <v-list-item-title
            class="font-weight-regular my-2 text--disabled text-body-2 text-center"
          >
            There are no entries on this Checklist.
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <draggable
        v-else
        v-model="editedChecklist.items"
        :options="{ disabled: !canDrag }"
      >
        <div
          v-for="(item, index) of editedChecklist.items"
          :key="item.id"
        >
          <v-divider v-if="index" />

          <v-list-item
            dense
          >
            <v-list-item-action>
              <div class="d-flex">
                <v-icon
                  left
                  :style="draggableStyle"
                  @mouseover="enableDraggable"
                  @mouseout="disableDraggable"
                >
                  mdi-reorder-horizontal
                </v-icon>

                <v-checkbox
                  v-model="item.done"
                  disabled
                />
              </div>
            </v-list-item-action>

            <v-list-item-content
              class="py-0"
            >
              <v-text-field
                :ref="`item-name-input--${item.id}`"
                v-model="item.name"
                class="cklst-text-input"
                dense
                :disabled="!editable || disableCustomList"
                hide-details
                outlined
                :rules="[rules.required]"
                @keydown.enter.prevent="_ => addItem(item.id)"
                @keyup.down="_ => navigateToNextItem(item.id)"
                @keyup.up="_ => navigateToPreviousItem(item.id)"
              />
            </v-list-item-content>

            <v-list-item-action
              v-if="!disableCustomList"
            >
              <v-btn
                :disabled="hasSingleItem || !editable"
                icon
                small
                @click="_ => removeItem(item.id)"
              >
                <v-icon>
                  mdi-delete
                </v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </div>
      </draggable>
    </v-list>

    <ImportDialog
      v-model="showImportChecklistDialog"
      @checklist-selected="onChecklistSelectedFromLibrary"
    />
  </v-card>
</template>

<script>
import draggable from 'vuedraggable'

import { rules } from '@/utilities/validation-rules'

import ImportDialog from '@/components/Checklist/ChecklistImportDialog.vue'

export default {
  name: 'ChecklistEditor',

  components: {
    ImportDialog,
    draggable,
  },

  props: {
    checklist: {
      type: Object,
      default: undefined,
    },

    disableCustomList: {
      type: Boolean,
      default: false,
    },

    editable: {
      default: true,
      required: false,
      type: Boolean,
    },
  },

  data () {
    return {
      canDrag: false,
      draggableStyle: 'cursor: move',
      editedChecklist: {
        items: [],
        title: '',
      },

      lastIdUsed: 0,
      rules,
      showImportChecklistDialog: false,
    }
  },

  computed: {
    hasItems () {
      return this.editedChecklist.items.length > 0
    },

    hasSingleItem () {
      return this.editedChecklist.items.length === 1
    },

    isNewChecklist () {
      return !this.checklist
    },
  },

  watch: {
    checklist: {
      handler: 'reloadChecklist',
      deep: true,
    },

    editedChecklist: {
      handler (value) {
        this.$emit('update:editedChecklist', {
          items: value.items.map(item => {
            return {
              done: item.done,
              name: item.name,
            }
          }),
          title: value.title,
        })
      },

      deep: true,
    },
  },

  created () {
    this.reloadChecklist(this.checklist)
  },

  methods: {
    addItem (previousItemId) {
      this.lastIdUsed++

      const item = {
        done: false,
        id: this.lastIdUsed,
        name: '',
      }

      if (this.editedChecklist.items.length === 0) {
        this.editedChecklist.title = '<untitled>'
        this.editedChecklist.items.push(item)
      } else {
        const previousItemIndex = this.editedChecklist.items.findIndex(i => i.id === previousItemId)
        const index = previousItemIndex < 0 ? this.editedChecklist.items.length : previousItemIndex + 1

        this.editedChecklist.items.splice(index, 0, item)
      }

      this.$nextTick().then(_ => {
        this.$refs[`item-name-input--${item.id}`][0].focus()
      })
    },

    deleteChecklist () {
      this.editedChecklist.title = ''
      this.editedChecklist.items = []
    },

    disableDraggable () {
      this.canDrag = false
      this.draggableStyle = 'cursor: inherit'
    },

    enableDraggable () {
      this.canDrag = (this.disableCustomList === undefined)
      this.draggableStyle = 'cursor: move'

      if (!this.canDrag) this.draggableStyle = 'cursor: inherit'
    },

    navigateToNextItem (currentItemId) {
      const index = this.editedChecklist.items.findIndex(i => i.id === currentItemId) + 1
      if (index >= this.editedChecklist.items.length) return
      this.$refs[`item-name-input--${this.editedChecklist.items[index].id}`][0].focus()
    },

    navigateToPreviousItem (currentItemId) {
      const index = this.editedChecklist.items.findIndex(i => i.id === currentItemId) - 1
      if (index < 0) return
      this.$refs[`item-name-input--${this.editedChecklist.items[index].id}`][0].focus()
    },

    onChecklistSelectedFromLibrary (checklist) {
      this.reloadChecklist(checklist)
      this.showImportChecklistDialog = false
    },

    openImportChecklistDialog () {
      this.showImportChecklistDialog = true
    },

    reloadChecklist (checklist) {
      this.editedChecklist.title = checklist?.title || ''
      this.editedChecklist.items = checklist?.items?.map(item => {
        this.lastIdUsed++
        return {
          done: item.done,
          id: this.lastIdUsed,
          name: item.name,
        }
      }) || []
    },

    removeItem (id) {
      const index = this.editedChecklist.items.findIndex(i => i.id === id)
      if (index < 0) return
      this.editedChecklist.items.splice(index, 1)
    },
  },
}
</script>

<style scoped>
  /deep/ .v-banner .v-banner__content,
  /deep/ .v-banner .v-banner__text {
    overflow: visible;
  }
  /deep/ .v-text-field--outlined fieldset {
    border-color: transparent;
  }
  /deep/ .v-text-field--outlined .v-input__slot:hover fieldset,
  /deep/ .v-text-field--outlined.v-input--is-focused fieldset {
    border-color: currentColor;
  }
  /deep/ .v-text-field--outlined.error--text {
    background-color: rgba(255, 77, 77, .2);
  }
  /deep/ .v-text-field--outlined.error--text fieldset {
    border-color: currentColor !important;
  }
  /deep/ .v-banner__text {
    text-wrap: pretty !important;
    white-space: pre-wrap;
  }
</style>
